import React from 'react'
import styled from 'styled-components'
import { useTheme } from '../../../middleware/ThemeContext';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import { TempSettings } from '../../../types/types';
import { hexToRgba } from '../../../utils/helper';
import StyledInput from '../../StyledComponents/StyledInput';
import { StyledHorizontalLine } from '../../StyledComponents/Styledhr';
import { Radio } from '@mui/material';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import StyledRadio from '../../StyledComponents/StyledRadio';
import AMSSelect from '../../AMSSelect';
import { Form } from 'react-bootstrap';
import LeftRightLayoutIcon from '../../../assets/icons/LeftRightLayoutIcon';
import RightLeftLayoutIcon from '../../../assets/icons/RightLeftLayoutIcon';
import CenterStackLayoutIcon from '../../../assets/icons/CenterStackLayoutIcon';
import StyledCheckBox from '../../StyledComponents/CheckBox/StyledCheckBox';
import CustomInputURL from '../../form/CustomInputURL';
import FacebookIcon from '../../../assets/icons/FacebookIcon';
import LinkedInIcon from '../../../assets/icons/LinkedInIcon';
import YoutubeIcon from '../../../assets/icons/YoutubeIcon';
import TwitterIcon from '../../../assets/icons/TwitterIcon';
import XIcon from '../../../assets/icons/XIcon';
import SqaureLinkedInIcon from '../../../assets/icons/SqaureLinkedInIcon';
import NewYoutubeIcon from '../../../assets/icons/NewYoutubeIcon';
import NewXIcon from '../../../assets/icons/NewXIcon';

const ButtonProperties = ({ updateTempSetting, tempSettings }: { updateTempSetting: (key: string, value: any) => void, tempSettings: TempSettings }) => {
    const { theme } = useTheme();
    const headingOptions = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
    const fontWeightOptions = ['Regular', 'Medium', 'Bold'];
    const textAlignmentOptions = [{ id: 'Left', icon: <LeftAlignIcon /> }, { id: 'Center', icon: <CenterAlignIcon /> }, { id: 'Right', icon: <RightAlignIcon /> }];
    const defaultOption = { value: 1, label: "Primary Menu (Footer)" };

    return (
        <Container theme={theme}>
            <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Content</StyledText>

            <Section theme={theme}>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Text</StyledText>
                <StyledInput
                    theme={theme}
                    $fontWeight='500'
                    color={theme.CorporateTheme.colors.headerColor}
                    value={tempSettings.content || tempSettings.htmlContent || ''}
                    placeholder="Click me"
                    onChange={(e) => {
                        const nextValue = e.target.value;
                        updateTempSetting('content', nextValue);
                        updateTempSetting('htmlContent', nextValue);
                    }}
                    style={{ background: theme.CorporateTheme.colors.background }}
                />
            </Section>

            <div className='mt-4 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>

            <Section theme={theme}>
                <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary} className='mb-4 mt-2'>Styling</StyledText>
                <div>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>Button Color</StyledText>
                    <Row style={{ alignItems: 'center', position: 'relative' }}>
                        {/* Invisible but functional color input */}
                        <input
                            type="color"
                            value={tempSettings.buttonColor || '#0050ff'}
                            onChange={(e) => updateTempSetting('buttonColor', e.target.value)}
                            style={{
                                width: '105px',
                                height: '42px',

                                // remove all browser styles
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',

                                opacity: 0,
                                cursor: 'pointer',
                                position: 'absolute',
                                zIndex: 2,

                                padding: 0,
                                margin: 0,
                            }}
                        />

                        {/* Visible preview box */}
                        <div
                            style={{
                                width: '105px',
                                height: '42px',
                                border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                                borderRadius: '8px',
                                backgroundColor: tempSettings.buttonColor || '#0050ff',
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />

                        <HexInput
                            theme={theme}
                            type="text"
                            value={tempSettings.buttonColor || '#0050ff'}
                            onChange={(e) => updateTempSetting('buttonColor', e.target.value)}
                        />
                    </Row>

                </div>
                <div>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>Button Text Color</StyledText>
                    <Row style={{ alignItems: 'center' }}>
                        <div style={{ position: 'relative', width: '105px', height: '42px' }}>
                            <input
                                type="color"
                                value={tempSettings.buttonTextColor || '#DDEBFF'}
                                onChange={(e) => updateTempSetting('buttonTextColor', e.target.value)}
                                style={{
                                    width: '105px',
                                    height: '42px',
                                    opacity: 0,
                                    cursor: 'pointer',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 2,
                                    borderRadius: '8px',
                                    padding: 0,
                                    margin: 0,
                                    border: 'none',
                                }}
                            />

                            <div
                                style={{
                                    width: '105px',
                                    height: '42px',
                                    border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                                    borderRadius: '8px',
                                    backgroundColor: tempSettings.buttonTextColor || '#DDEBFF', // 🔴 Fix: was textColor!
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            />
                        </div>

                        {/* Hex input */}
                        <HexInput
                            theme={theme}
                            type="text"
                            value={tempSettings.buttonTextColor || '#DDEBFF'}
                            onChange={(e) => updateTempSetting('buttonTextColor', e.target.value)}
                        />
                    </Row>
                </div>

                <div>
                    <StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mb-2'>Button Hover Color</StyledText>
                    <Row style={{ alignItems: 'center' }}>
                        <div style={{ position: 'relative', width: '105px', height: '42px' }}>
                            <input
                                type="color"
                                value={tempSettings.buttonHoverColor || '#003FC7'}
                                onChange={(e) => updateTempSetting('buttonHoverColor', e.target.value)}
                                style={{
                                    width: '105px',
                                    height: '42px',
                                    opacity: 0,
                                    cursor: 'pointer',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    zIndex: 2,
                                    borderRadius: '8px',
                                    padding: 0,
                                    margin: 0,
                                    border: 'none',
                                }}
                            />

                            <div
                                style={{
                                    width: '105px',
                                    height: '42px',
                                    border: `1px solid ${theme.CreateFormTheme.colors.inputBorders}`,
                                    borderRadius: '8px',
                                    backgroundColor: tempSettings.buttonHoverColor || '#003FC7', // 🔴 Fix: was textColor!
                                    position: 'relative',
                                    zIndex: 1,
                                }}
                            />
                        </div>

                        {/* Hex input */}
                        <HexInput
                            theme={theme}
                            type="text"
                            value={tempSettings.buttonHoverColor || '#003FC7'}
                            onChange={(e) => updateTempSetting('buttonHoverColor', e.target.value)}
                        />
                    </Row>
                </div>
            </Section>
            <div className='mt-2 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>
            <Section theme={theme}>
                <StyledText fontSize='13px' fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Button Alignment</StyledText>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 15 }}>
                    {textAlignmentOptions.map((alignment) => {
                        const value = alignment.id.toLowerCase();

                        return (
                            <AlignmentCard
                                key={value}
                                theme={theme}
                                $isSelected={tempSettings.buttonAlignment === value}
                                onClick={() => updateTempSetting('buttonAlignment', value)}
                            >
                                <StyledRadio borderWidth='2px' radioColor={theme.CorporateTheme.colors.primary} name="alignment" checked={tempSettings.buttonAlignment === value} onChange={() => { }} label="" />
                                {alignment.icon}
                            </AlignmentCard>
                        );
                    })}
                </div>
            </Section>
            <div className='mt-2 pt-2'>
                <StyledHorizontalLine theme={theme} />
            </div>
            <Section theme={theme}>
                <div className='col mt-3'>
                    <div className='d-flex align-items-center gap-1 mb-2'><StyledText fontSize='14px' fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor}>Interactivity</StyledText>
                        <StyledText fontSize='14px' fontWeight={400} textColor={theme.CorporateTheme.colors.headerColor}>(Link)</StyledText></div>
                    <CustomInputURL
                        value={tempSettings.buttonLink}
                        onChange={(value) => {
                            updateTempSetting('buttonLink', value);
                        }}

                    />
                </div>
                <div className='mt-2'>
                    <StyledCheckBox
                        className="cursor-pointer d-flex align-items-center justify-content-center rounded-1"
                        size={16}
                        borderColor={
                            tempSettings.openNewTab
                                ? theme.CorporateTheme?.colors?.primary
                                : theme.CorporateTheme?.colors?.headerColor
                        }
                        bgColor={theme.CorporateTheme?.colors?.primary}
                        checkColor={theme.colors.contentBackground}
                        value={tempSettings.openNewTab}
                        onChange={() => updateTempSetting('openNewTab', !tempSettings.openNewTab)}
                        label="Want to open this in new tab?"
                    />
                </div>
            </Section>

        </Container>
    )
}

const Container = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 16px;
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    `

const Section = styled.div<{ theme: Theme }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 26px;
`;


const OptionsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const OptionButton = styled.button<{ theme: Theme, $isSelected?: boolean }>`
    flex: 1;
    padding: 8px 12px;
    border: 1px solid ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.border};
    background-color: ${({ theme, $isSelected }) =>
        $isSelected ? hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1) : theme.CorporateTheme.colors.background};
    color: ${({ theme, $isSelected }) =>
        $isSelected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    text-align: center;
    
    /* Remove margin */
    margin: 0;
    
    /* Default no border-radius */
    border-radius: 0;
    
    /* First button - rounded left corners */
    &:first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    /* Last button - rounded right corners */
    &:last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
    }

    /* Remove double borders between buttons */
    &:not(:first-child) {
        margin-left: -1px;
    }

    /* IMPORTANT: Set z-index for all buttons to prevent overlap issues */
    position: relative;
    z-index: ${({ $isSelected }) => $isSelected ? 2 : 1};

    &:hover {
        background-color: ${({ theme }) => hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        color: ${({ theme }) => theme.CorporateTheme.colors.accentOrange};
        z-index: 3; /* Higher z-index on hover */
    }

    /* Selected state styling */
    ${({ $isSelected, theme }) => $isSelected && `
        background-color: ${hexToRgba(theme.CorporateTheme.colors.accentOrange, 0.1)};
        border-color: ${theme.CorporateTheme.colors.accentOrange};
        color: ${theme.CorporateTheme.colors.accentOrange};
        font-weight: 600;
        z-index: 2;
    `}
`;

const ColorBox = styled.div<{ color: string }>`
  width: 90px;
  height: 50px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;
  background: ${(p) => p.color};
`;

const HexInput = styled(Form.Control)`
flex: 1;
  height: 42px;
    background: transparent;
  color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
`;

const ColorInput = styled.input`
  width: 105px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #dcdcdc;   /* ← keep outer border if you want it */
  outline: none;
  box-shadow: none;
  cursor: pointer;
  padding: 0;                /* ✅ Critical: creates inner margin */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
`;



const Row = styled.div`
  display: flex;
    gap: 10px;
  align-items: center;
  margin-bottom: 20px;
`;

const TextInput = styled.input<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.background};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    &::placeholder {
        color: ${({ theme }) => theme.CorporateTheme.colors.textSecondary};
    }
`;

const Select = styled.select<{ theme: Theme }>`
    padding: 10px 12px;
    border-radius: 4px;
    border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
    background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
    color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    font-size: 14px;
    
    &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.CorporateTheme.colors.primary};
    }
    
    option {
        background-color: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
        color: ${({ theme }) => theme.CorporateTheme.colors.textPrimary};
    }
`;


const AlignmentCard = styled.button<{
    theme: any;
    $isSelected: boolean;
}>`
  display: flex;
  align-items: center;
    border:none;
  cursor: pointer;
    gap: 6px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};

  transition: all 0.2s ease;


`;



export default ButtonProperties
